import {
  CanvasCard,
  SuggestedCanvasCard,
  useApproveCanvasSuggestion,
  useCanvasSuggestions,
  useCanvases,
  useRejectCanvasSuggestion,
} from "@/features/canvases";
import { useCapability, useHasCapability } from "@/features/capabilities";
import { validateWorkspaceSearch } from "@/features/workspace-router";
import type { CanvasStatusFilter } from "@ando/core";
import {
  SegmentedTabs,
  SurfaceCommandBar,
  SurfaceEmptyState,
  SurfaceLayout,
  SurfaceList,
  cn,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Outlet, createFileRoute, useMatchRoute } from "@tanstack/react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { BookOpenText, Lock, Sparkles } from "lucide-react";
import { type FC, type ReactNode, useMemo, useState } from "react";

/**
 * Canvases hub — Knowledge Canvases overview.
 *
 * v1 ref: ``ando-frontend/app/pages/CanvasesHubPage.tsx`` (250 LoC).
 *
 * Capability-gated via ``useHasCapability("canvases")``: the feature is
 * scoped to tenants on the Knowledge tier (BE: ``_require_canvases_access``
 * in ``canvases.py``). Loading state defaults to ``false`` (fail-closed)
 * so an unentitled user never sees a flash of the gated view.
 *
 * Route slots into the workspace shell (``_authed/route.tsx`` `<Outlet />`)
 * but does NOT live in ``WORKSPACE_TAB_DEFS`` (the 6 top tabs are a fixed
 * v18-mockup contract). Navigation is deep-link only for now; the
 * Companion Hero / Studio drawer can surface the entry point in
 * Phase F-LATER.
 */
export const Route = createFileRoute("/_authed/workspace/canvases")({
  validateSearch: validateWorkspaceSearch,
  component: CanvasesHub,
});

/**
 * Inline pill toggle used by the per-type suggestion filter. Kept local
 * (vs lifting to ``@ando/ui``) because this is the only consumer today
 * and the styling references brand tokens directly — promoting it would
 * need a wider design contract first.
 */
const FilterChip: FC<{
  label: ReactNode;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={cn(
      "inline-flex h-7 items-center rounded-full px-2.5 font-medium text-[11px] transition-colors",
      active
        ? "bg-ando-orange-soft text-ando-orange"
        : "text-ink-3 hover:bg-black/[0.04] hover:text-ink-1",
    )}
  >
    {label}
  </button>
);

function CanvasesHub() {
  const { i18n } = useLingui();
  // Detail-route delegation. ``canvases.$canvasId.tsx`` is registered as a
  // CHILD of this route (TanStack file-based: dot-nested), so the router
  // mounts this parent and tries to render the child inside an ``<Outlet/>``.
  // Without delegation the URL changes but the hub keeps rendering and the
  // child stays silent — "click su Mexal/ThingWorx non apre nulla" (May 30,
  // 2026). The early-return happens AFTER all hooks so React's hook count
  // stays stable across list↔detail transitions.
  const matchRoute = useMatchRoute();
  const isDetail = matchRoute({
    to: "/workspace/canvases/$canvasId",
    fuzzy: true,
  });

  const hasAccess = useHasCapability("canvases");
  const { state: capState } = useCapability("canvases");
  const [statusTab, setStatusTab] = useState<CanvasStatusFilter>("active");
  // Per-type filter for the suggestions section — null = no filter (all).
  // ``entity_type`` values are discovered from the payload, never hardcoded:
  // BE may add new types (May 26 the catalog grew with ``CONCEPT``) and the
  // FE should follow without a redeploy.
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  // Progressive disclosure — show top N by score, expand on demand. Caps
  // the visual weight at the hub even when the user has 100+ suggestions
  // pending. ``VISIBLE_DEFAULT`` is tuned so the section stays under one
  // viewport on a 13" laptop alongside the canvases list.
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(false);
  const reducedMotion = useReducedMotion();
  const { canvases, total, isLoading, isError, refetch } = useCanvases({
    enabled: hasAccess,
    params: { status: statusTab, limit: 50 },
  });

  // Suggested canvases — Step 3 of the multi-signal pipeline.
  // Only render on the "active" tab: archived view is a maintenance
  // surface, suggestions there would be noise (May 25, 2026).
  const showSuggestions = hasAccess && statusTab === "active";
  const { suggestions, isLoading: suggestionsLoading } = useCanvasSuggestions({
    enabled: showSuggestions,
  });
  const approve = useApproveCanvasSuggestion();
  const reject = useRejectCanvasSuggestion();
  // Track which row is currently mutating so the spinner shows on the
  // right card (NOT every card at once — common pitfall when you bind
  // the mutation isPending flag globally).
  const pendingApproveId =
    approve.isPending && typeof approve.variables === "string" ? approve.variables : null;
  const pendingRejectId =
    reject.isPending && typeof reject.variables === "string" ? reject.variables : null;

  // Available entity_types from the current payload (sorted, deduped).
  // Filter chips only render when there are ≥2 types — with a single type
  // the chip strip would just be noise (e.g., 8 PROJECT suggestions, no
  // value in offering a "PROJECT" pill that's always on).
  const availableTypes = useMemo(
    () => Array.from(new Set(suggestions.map((s) => s.entity_type))).sort(),
    [suggestions],
  );
  const filteredSuggestions = useMemo(
    () => (typeFilter ? suggestions.filter((s) => s.entity_type === typeFilter) : suggestions),
    [suggestions, typeFilter],
  );
  const VISIBLE_DEFAULT = 5;
  const visibleSuggestions = suggestionsExpanded
    ? filteredSuggestions
    : filteredSuggestions.slice(0, VISIBLE_DEFAULT);
  const hiddenCount = filteredSuggestions.length - visibleSuggestions.length;

  // Hook-stable early return — keep this AFTER every hook call above so
  // React's hook order is identical across renders regardless of route.
  if (isDetail) return <Outlet />;

  // Capability still loading → render the skeleton meta so the user doesn't
  // see a "no access" flash on first paint.
  if (capState === "loading") {
    return (
      <SurfaceLayout
        header={
          <SurfaceCommandBar
            count={
              <span className="text-ink-3">
                <Trans>Caricamento…</Trans>
              </span>
            }
          />
        }
      >
        <div />
      </SurfaceLayout>
    );
  }

  if (!hasAccess) {
    return (
      <SurfaceLayout header={<SurfaceCommandBar />}>
        <SurfaceEmptyState
          icon={<Lock size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Canvases non attivi sul tuo piano`)}
          description={i18n._(
            msg`I Knowledge Canvases sono inclusi nei piani Knowledge e superiori. Contatta il tuo amministratore per attivarli.`,
          )}
        />
      </SurfaceLayout>
    );
  }

  const meta = isLoading ? (
    <span className="text-ink-3">
      <Trans>Caricamento…</Trans>
    </span>
  ) : (
    <Trans>
      <strong className="tabular-nums">{total}</strong>{" "}
      {statusTab === "archived" ? i18n._(msg`archiviati`) : i18n._(msg`canvases`)}
    </Trans>
  );

  return (
    <SurfaceLayout
      header={
        <SurfaceCommandBar
          views={
            <SegmentedTabs
              ariaLabel={i18n._(msg`Filtro stato canvases`)}
              value={statusTab}
              onChange={setStatusTab}
              options={[
                { value: "active", label: i18n._(msg`Attivi`) },
                { value: "archived", label: i18n._(msg`Archiviati`) },
              ]}
            />
          }
          count={meta}
        />
      }
    >
      {isError ? (
        <SurfaceEmptyState
          icon={<BookOpenText size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Impossibile caricare i canvases`)}
          description={i18n._(msg`Riprova fra qualche secondo.`)}
          action={
            <button
              type="button"
              onClick={() => void refetch()}
              className="hover:-translate-y-px rounded-md bg-ink-2 px-3 py-1.5 text-[12px] text-white"
            >
              <Trans>Riprova</Trans>
            </button>
          }
        />
      ) : (
        <>
          {/* Suggested canvases — appears/disappears with a soft slide so
              new suggestions arriving via 60s polling feel intentional
              instead of a content jump. ``layout`` animates the
              subsequent list shift when rows are approved/rejected.
              ``useReducedMotion()`` collapses to instant for users with
              the OS-level preference. */}
          <AnimatePresence initial={false}>
            {showSuggestions && suggestions.length > 0 ? (
              <motion.section
                key="suggestions"
                layout
                initial={reducedMotion ? false : { opacity: 0, y: -6, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, height: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                aria-labelledby="canvas-suggestions-heading"
                className="mx-8 mt-2 mb-4 overflow-hidden"
              >
                <div className="mb-2 flex flex-wrap items-baseline gap-2">
                  <h2
                    id="canvas-suggestions-heading"
                    className="inline-flex items-center gap-1.5 font-semibold text-[12px] text-ink-1 uppercase tracking-[0.06em]"
                  >
                    <Sparkles size={13} strokeWidth={2} className="text-ando-orange" aria-hidden />
                    <Trans>Consigliati da Ando</Trans>
                  </h2>
                  <span className="rounded-full bg-ando-orange-soft px-1.5 font-semibold text-[10.5px] text-ando-orange tabular-nums">
                    {filteredSuggestions.length}
                  </span>
                  {/* Per-type filter — only useful when there's actual
                      heterogeneity in the suggested entities. Discovered
                      from the payload so new BE types appear automatically.
                      ``flex-wrap`` so 4+ types stack onto a second row
                      instead of pushing the strip into hidden overflow
                      (May 31, 2026 UX review). */}
                  {availableTypes.length >= 2 ? (
                    <div
                      role="tablist"
                      aria-label={i18n._(msg`Filtra suggerimenti per tipo`)}
                      className="ml-auto flex flex-wrap items-center justify-end gap-1"
                    >
                      <FilterChip
                        active={typeFilter === null}
                        onClick={() => setTypeFilter(null)}
                        label={i18n._(msg`Tutti · ${suggestions.length}`)}
                      />
                      {availableTypes.map((t) => (
                        <FilterChip
                          key={t}
                          active={typeFilter === t}
                          onClick={() => setTypeFilter(typeFilter === t ? null : t)}
                          label={t.toLowerCase()}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
                {/* Per-row AnimatePresence: approve/reject removes the
                    card with a slide-out instead of a snap, so the
                    user sees their action confirmed. */}
                <motion.ul layout className="space-y-1">
                  <AnimatePresence initial={false}>
                    {visibleSuggestions.map((s) => (
                      <motion.div
                        key={s.id}
                        layout
                        initial={reducedMotion ? false : { opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, x: 12, height: 0 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                      >
                        <SuggestedCanvasCard
                          suggestion={s}
                          onApprove={(id) => approve.mutate(id)}
                          onReject={(id) => reject.mutate(id)}
                          isApproving={pendingApproveId === s.id}
                          isRejecting={pendingRejectId === s.id}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.ul>
                {/* Progressive disclosure — only show the toggle when there
                    actually IS overflow. Same chrome whether expanding or
                    collapsing so the click target stays predictable. */}
                {filteredSuggestions.length > VISIBLE_DEFAULT ? (
                  <button
                    type="button"
                    onClick={() => setSuggestionsExpanded((v) => !v)}
                    className={cn(
                      "mt-1.5 inline-flex h-7 items-center gap-1 rounded-md px-2",
                      "font-medium text-[11.5px] text-ando-orange",
                      "transition-colors hover:bg-ando-orange-soft",
                      "focus-visible:outline-none focus-visible:ring-2",
                      "focus-visible:ring-ando-orange focus-visible:ring-offset-1",
                      "focus-visible:ring-offset-cream",
                    )}
                  >
                    {suggestionsExpanded
                      ? i18n._(msg`Mostra meno`)
                      : i18n._(msg`Mostra altri ${hiddenCount} →`)}
                  </button>
                ) : null}
              </motion.section>
            ) : null}
          </AnimatePresence>

          {!isLoading && canvases.length === 0 ? (
            // Hub-level empty state — but only "true empty" when there
            // are ALSO no suggestions to act on. Otherwise the section
            // above is the action surface; here we just say "list empty".
            suggestions.length === 0 ? (
              <SurfaceEmptyState
                icon={<BookOpenText size={18} strokeWidth={1.75} />}
                title={
                  statusTab === "archived"
                    ? i18n._(msg`Nessun canvas archiviato`)
                    : i18n._(msg`Ancora nessun canvas`)
                }
                description={
                  statusTab === "archived"
                    ? i18n._(msg`Gli archiviati appariranno qui — puoi sempre riattivarli.`)
                    : suggestionsLoading
                      ? i18n._(msg`Stiamo cercando entità ricorrenti nelle tue conversazioni…`)
                      : i18n._(
                          msg`Continua a chattare con Ando: appena emerge un'entità ricorrente, ti propone un canvas qui sopra.`,
                        )
                }
              />
            ) : (
              <div className="mx-8 mb-6 text-[12px] text-ink-3">
                <Trans>
                  Nessun canvas creato — approva un suggerimento qui sopra per iniziare.
                </Trans>
              </div>
            )
          ) : (
            <SurfaceList flat label={i18n._(msg`Canvases`)}>
              {canvases.map((c) => (
                <li key={c.id}>
                  <CanvasCard canvas={c} />
                </li>
              ))}
            </SurfaceList>
          )}
        </>
      )}
    </SurfaceLayout>
  );
}
